import { useEffect, useRef } from "react";
import { useTaskCheck } from "@/core/hooks/useTaskCheck";
import type { LessonStep } from "@/core/types";
import { CheckFeedback } from "./CheckFeedback";
import { Icon } from "./Icon";
import { MacroPanel } from "./MacroPanel";

interface StepCardProps {
  step: LessonStep;
  isDone: boolean;
  isLast: boolean;
  onPrev?: () => void;
  onCompleted(): void;
  onNext(): void;
}

/**
 * Ein Schritt einer Lektion: Erklärung, Merkpunkte, Aufgabe und Prüfung.
 * Die Komponente wird pro Schritt neu erzeugt, die Rückmeldung einer
 * früheren Prüfung bleibt also nie stehen.
 */
export function StepCard({ step, isDone, isLast, onPrev, onCompleted, onNext }: StepCardProps) {
  const { status, result, run } = useTaskCheck(step.task?.check);
  const feedbackRef = useRef<HTMLDivElement>(null);

  // Die Rückmeldung in den sichtbaren Bereich holen – die Aktionsleiste
  // unten verdeckt sonst ihren Anfang.
  useEffect(() => {
    if (result) feedbackRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [result]);

  const handleCheck = async () => {
    const outcome = await run();
    if (outcome?.passed) onCompleted();
  };

  const nextLabel = isLast ? "Lektion abschließen" : "Weiter";
  const running = status === "running";

  return (
    <article className="step" aria-labelledby={`schritt-${step.id}`}>
      <div className="step__body">
        <h2 className="step__title" id={`schritt-${step.id}`}>
          {step.title}
        </h2>

        <div className="prose">
          {step.explanation.map((paragraph) => (
            <p key={paragraph.slice(0, 40)}>{paragraph}</p>
          ))}
        </div>

        {step.keyPoints && step.keyPoints.length > 0 ? (
          <aside className="keypoints" aria-label="Auf einen Blick">
            <h3 className="keypoints__title">Auf einen Blick</h3>
            <ul>
              {step.keyPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </aside>
        ) : null}

        {step.task ? (
          <section className={`task ${isDone ? "is-done" : ""}`} aria-label="Deine Aufgabe">
            <h3 className="task__title">
              <Icon name="task" />
              Deine Aufgabe
              {isDone ? (
                <span className="pill pill--positive">
                  <Icon name="check" size={12} />
                  Erledigt
                </span>
              ) : null}
            </h3>
            <p className="task__instruction">{step.task.instruction}</p>

            {step.task.hint ? (
              <details className="disclosure disclosure--inline">
                <summary>
                  <Icon name="bulb" />
                  <span>Tipp anzeigen</span>
                  <Icon name="chevronDown" className="disclosure__chevron" />
                </summary>
                <p className="disclosure__content">{step.task.hint}</p>
              </details>
            ) : null}

            {step.task.macro ? (
              <MacroPanel macro={step.task.macro} nurErsatz={step.task.macroNurErsatz} />
            ) : null}
          </section>
        ) : null}

        <div ref={feedbackRef} className="feedback-slot">
          {result ? (
            <CheckFeedback
              result={result}
              onMarkDone={result.unsupported && !isDone ? onCompleted : undefined}
            />
          ) : null}
        </div>
      </div>

      <div className="actionbar">
        {onPrev ? (
          <button
            type="button"
            className="btn btn--secondary btn--icon"
            onClick={onPrev}
            aria-label="Vorheriger Schritt"
            title="Vorheriger Schritt"
          >
            <Icon name="chevronLeft" />
          </button>
        ) : null}

        {step.task ? (
          isDone ? (
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void handleCheck()}
                disabled={running}
              >
                {running ? <span className="spinner" aria-hidden="true" /> : null}
                Erneut prüfen
              </button>
              <button type="button" className="btn btn--primary btn--grow" onClick={onNext}>
                {nextLabel}
                <Icon name="arrowRight" />
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn--primary btn--grow"
              onClick={() => void handleCheck()}
              disabled={running}
              aria-busy={running}
            >
              {running ? <span className="spinner" aria-hidden="true" /> : <Icon name="check" />}
              {running ? "Wird geprüft …" : "Aufgabe prüfen"}
            </button>
          )
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--grow"
            onClick={() => {
              onCompleted();
              onNext();
            }}
          >
            {isLast ? "Verstanden, abschließen" : "Verstanden, weiter"}
            <Icon name="arrowRight" />
          </button>
        )}
      </div>
    </article>
  );
}
